import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessNutcracker } from '@hooks/register/screening/use-assess-nutcracker';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessNutcrackerSchema } from '@views/register/screening/assess-nutcracker.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import { asStringBool, asStringYesNoUnknown } from '@/utils/parse';
import type { IAssessNutcrackerFormValues, IAssessNutcrackerModel, IAssessNutcrackerSubmitResult } from '@/types/screening/assess-nutcracker';
import PageLoader from '@components/page-loader';
import ScreeningProgress from '@views/register/screening/screening-progress';
import FieldError from '@components/field-error';
import RequiredFieldsAlert from '@components/required-fields-alert';
import NavigationButtons from '../screening-history-navigation-buttons';

const AssessNutcracker = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessNutcracker(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessNutcrackerContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessNutcrackerContentProps {
    model: IAssessNutcrackerModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessNutcrackerFormValues) => Promise<IAssessNutcrackerSubmitResult>;
}

const AssessNutcrackerContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessNutcrackerContentProps) => {
    const t = useLocalize(model.languageData);

    const {
        register,
        watch,
        onSubmit: handleFormSubmit,
        formState: { errors },
    } = useStepForm<IAssessNutcrackerFormValues, IAssessNutcrackerSubmitResult>({
        resolver: zodResolver(assessNutcrackerSchema),
        defaultValues: {
            hasNutcracker: asStringYesNoUnknown(model.hasNutcracker),
            wasNutcrackerTreated: asStringBool(model.wasNutcrackerTreated),
            nutcrackerTreatment: model.nutcrackerTreatment,
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const showStatus = watch('hasNutcracker') === '1';
    const showTreatment = watch('wasNutcrackerTreated') === '1';

    return (
        <div className="row">
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <div className="col-xs-12 col-sm-3 hidden-xs">
                <ScreeningProgress model={model} overrideStep={DashRegistrationStep.AssessNutcracker} />
            </div>

            <div className="col-xs-12 col-sm-5 summary-mobile-content">
                <RequiredFieldsAlert show={Object.keys(errors).length > 0} t={t} />

                <h1>{t('DashLabel_ProgressNutcracker', 'Nutcracker Syndrome')}</h1>

                <hr />

                <form className="form-horzontal" onSubmit={handleFormSubmit}>
                    <div className="row row-margined">
                        <div className="col-xs-12">
                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {t('DashLabel_HasNutcracker', 'Do you have Nutcracker Syndrome')}?
                                    <FieldError message={errors.hasNutcracker?.message} />
                                </label>
                                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                    <label className="radio-inline">
                                        <input type="radio" value="1" {...register('hasNutcracker')} />{' '}
                                        {t('Word_Yes', 'Yes')}
                                    </label>
                                    <label className="radio-inline">
                                        <input type="radio" value="0" {...register('hasNutcracker')} />{' '}
                                        {t('Word_No', 'No')}
                                    </label>
                                    <label className="radio-inline">
                                        <input type="radio" value="-1" {...register('hasNutcracker')} />{' '}
                                        {t('DashLabel_IDonTKnow', "I don't know")}
                                    </label>
                                </div>
                            </div>

                            {showStatus && (
                                <div className="sub-question" id="status">
                                    <div className="form-group">
                                        <label className="control-label col-xs-12">
                                            {t(
                                                'DashLabel_WasNutcrackerTreated',
                                                'Have you ever received treatment for Nutcracker Syndrome',
                                            )}
                                            ?<FieldError message={errors.wasNutcrackerTreated?.message} />
                                        </label>
                                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                            <label className="radio-inline">
                                                <input
                                                    type="radio"
                                                    value="1"
                                                    {...register('wasNutcrackerTreated')}
                                                />{' '}
                                                {t('Word_Yes', 'Yes')}
                                            </label>
                                            <label className="radio-inline">
                                                <input
                                                    type="radio"
                                                    value="0"
                                                    {...register('wasNutcrackerTreated')}
                                                />{' '}
                                                {t('Word_No', 'No')}
                                            </label>
                                        </div>
                                    </div>

                                    {showTreatment && (
                                        <div className="sub-question" id="treatment">
                                            <div className="form-group">
                                                <label className="control-label col-xs-12">
                                                    {t(
                                                        'DashLabel_NutcrackerTreatment',
                                                        'Please list form of treatment',
                                                    )}
                                                    ?
                                                    <FieldError
                                                        message={errors.nutcrackerTreatment?.message}
                                                    />
                                                </label>
                                                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                                                    <input
                                                        className="form-control"
                                                        maxLength={255}
                                                        {...register('nutcrackerTreatment')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {submitError && (
                                <div className="form-group col-xs-12">
                                    <div className="alert alert-danger">{submitError}</div>
                                </div>
                            )}

                            <NavigationButtons
                                registrationId={model.id}
                                editKey={model.editKey}
                                isFemale={model.isFemale}
                                isLiver={model.isLiver}
                                currentRegistrationStep={model.currentRegistrationStep}
                                t={t}
                                isSubmitting={isSubmitting}
                                isScreening={true}
                            />
                        </div>
                    </div>
                </form>
            </div>

            <div className="col-xs-12 col-sm-3">
                <div style={{ paddingTop: 20 }}>
                    <h4 id="progress">{t('DashLabel_AboutNutcrackerSyndrome', 'About Nutcracker Syndrome')}</h4>

                    Nutcracker syndrome is a rare vascular condition where the left renal vein is squeezed between the abdominal aorta and the superior
                    mesenteric artery. This pinch raises blood pressure in the vein, causing blood to flow backward. It leads to symptoms like side or
                    lower back pain and blood in the urine.

                    <div style={{ padding: '60% 0 0 0', position: 'relative' }}>
                        <iframe src="https://player.vimeo.com/video/1123696711?h=0&title=0&byline=0&portrait=0" allow="autoplay; fullscreen; picture-in-picture" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: '0 none' }} title="Tour of Ibiza"></iframe>
                    </div>

                    <div style={{ paddingTop: 5 }}>
                        <a
                            href="https://www.kidneyregistry.com/for-donors/i-want-to-learn-more-about-living-kidney-donation/resources-for-individuals-with-nutcracker-syndrome/"
                            role="button"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t(
                                'DashLabel_LearnMoreAboutNutcracker',
                                'Click here to learn more about Nutcracker Syndrome',
                            )}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssessNutcracker;
